// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react'
import { getCookie } from '@node-space/rest-api'
import General from 'constants/General'
import { useChatBotQuery } from 'hooks/queries/useChatBot'
import { ZendeskWidget } from 'types/zendesk'
import { logSentryError } from 'utils/sentry'
import { decodeJwtPayload, isWithinMinutesFromNow, TEN_MINUTES } from 'utils/utils'

declare global {
  interface Window {
    zE?: ZendeskWidget
  }
}
interface ChatbotProps {
  isAuthed: boolean
}

const ChatBot = ({ isAuthed }: ChatbotProps) => {
  const [cookieAuthToken, setCookieAuth] = useState(() => getCookie(General.ZENDESK_COOKIE_NAME))
  const { data: token, refetch, isError, error, isFetching } = useChatBotQuery(isAuthed)
  const availableToken = token || (isError && !isFetching ? cookieAuthToken : null)
  const expiryTime = availableToken ? (decodeJwtPayload(availableToken)?.exp as number) : null

  const loggedInTokenRef = useRef<string | null>(null)
  const tokenRef = useRef(availableToken)
  tokenRef.current = availableToken

  const loginUser = (authToken: string) => {
    if (!authToken || loggedInTokenRef.current === authToken) return
    loggedInTokenRef.current = authToken
    window.zE('messenger', 'loginUser', callback => {
      callback(authToken)
    })
  }

  const loadChatbotScript = () => {
    const scriptId = 'ze-snippet'
    if (document.getElementById(scriptId)) {
      if (window?.zE) {
        loginUser(tokenRef.current)
      }
      return
    }

    const zendeskScript = document.createElement('script')
    zendeskScript.src = process.env.ZENDESK_CHATBOT_SCRIPT_URL
    zendeskScript.async = true
    zendeskScript.id = scriptId
    document.body.appendChild(zendeskScript)

    zendeskScript.onload = () => {
      if (window?.zE) {
        loginUser(tokenRef.current)
        window.zE('messenger:set', 'zIndex', 29)
      }
    }
  }

  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentCookie = getCookie(General.ZENDESK_COOKIE_NAME)

      if (currentCookie !== cookieAuthToken) {
        setCookieAuth(currentCookie)
      }

      if (expiryTime && isWithinMinutesFromNow(expiryTime, 15)) {
        refetch()
      }
    }, TEN_MINUTES)

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [cookieAuthToken])

  useEffect(() => {
    if (isAuthed && !availableToken) {
      refetch()
    }

    if (availableToken && isAuthed) {
      loadChatbotScript()
    }
    if (isError && error) {
      logSentryError('Error: useChatBotQuery', error)
    }
  }, [availableToken, refetch, isAuthed])

  return <></>
}

export default ChatBot
